import apiClient from '../api/client';

// =====================================================================
// Types
// =====================================================================

export interface StandardiseRequest {
  smiles?: string;
  molblock?: string;
}

export interface StandardiseResult {
  canonical_smiles: string;
  inchi: string;
  inchi_key: string;
  mol_formula: string;
  mol_weight: number;
  exact_mass: number;
  num_stereocenters: number;
  num_defined_stereocenters: number;
  has_undefined_stereo: boolean;
  num_heavy_atoms: number;
  full_smiles: string;
  full_inchi_key: string;
  full_mol_weight: number;
  salts_removed: { name: string; smiles: string }[];
  warnings: string[];
  parent_molblock?: string;
}

export interface UniquenessRequest {
  smiles?: string;
  molblock?: string;
}

export interface UniquenessResult {
  is_unique: boolean;
  matched_parent_code?: string;
  matched_parent_id?: number;
  matched_status?: string;
  existing_versions?: number;
  existing_lots?: number;
}

export interface RegisterRequest {
  smiles?: string;
  molblock?: string;
  therapeutic_area?: string;
  notes?: string;
  project_name?: string;
}

export interface RegisterResult {
  parent_code: string;
  version_code: string;
  lot_code: string;
  action: string;
  message: string;
}

export interface RegisteredLot {
  lot_code: string;
  batch_number: string | null;
  purity_percent: number | null;
  ee_percent: number | null;
  ee_method: string | null;
  supplier: string | null;
  created_at: string;
}

export interface RegisteredVersion {
  version_code: string;
  salt_name: string | null;
  salt_smiles: string | null;
  supplier: string | null;
  is_candidate: boolean;
  dev_code: string | null;
  lots: RegisteredLot[];
  created_at: string;
}

export interface RegisteredCompound {
  parent_code: string;
  canonical_smiles: string;
  inchi_key: string;
  mol_formula: string | null;
  mol_weight: number | null;
  exact_mass: number | null;
  status: string;
  therapeutic_area: string | null;
  therapeutic_area_name: string | null;
  notes: string | null;
  created_by_name?: string | null;
  created_at: string;
  version_count?: number;
  lot_count?: number;
  versions?: RegisteredVersion[];
}

export interface RegistrationRules {
  tenant_id: number;
  org_code: string;
  stereo_matters: boolean;
  salt_creates_version: boolean;
  auto_register: boolean;
  // Pipeline policies
  tautomer_policy: string;       // canonicalise | preserve
  stereo_policy: string;         // preserve | strip
  salt_strip_policy: string;     // strip | keep
  neutralise_policy: string;     // neutralise | reionise | preserve
  aromatise_policy: string;      // aromatise | kekulise | preserve
  normalise_groups: boolean;
  disconnect_metals: boolean;
  // Validation
  mw_max: number | null;
  must_have_structure: boolean;
  reject_invalid_valence: boolean;
  // Permissions
  promote_role: string;          // member | admin | super_admin
}

export interface Salt {
  salt_id: number;
  name: string;
  smiles: string;
  smarts: string | null;
  salt_type: string;
  common: boolean;
  category: string | null;
  charge: number | null;
  formula: string | null;
  is_ion: boolean;
  enabled: boolean;
  sort_order: number;
}

export interface TherapeuticArea {
  code: string;
  name: string;
  description: string | null;
}

export interface RegistrationStats {
  total_parents: number;
  total_versions: number;
  total_lots: number;
  by_status: Record<string, number>;
  by_therapeutic_area: Record<string, number>;
}

export interface StagingItem {
  id: number;
  smiles: string;
  status: string;
  created_at: string;
}

export interface StagingCounts {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

export interface AuditEntry {
  audit_id: number;
  action: string;
  entity_type: string;
  entity_id: number;
  entity_code: string | null;
  details: Record<string, any> | null;
  performed_by: string | null;
  performed_by_name: string | null;
  performed_at: string;
  canonical_smiles: string | null;
}

// =====================================================================
// API Client
// =====================================================================

const BASE = '/v1/registration';

export const registrationAPI = {
  // Standardise a SMILES structure
  standardise: (data: StandardiseRequest) =>
    apiClient.post<StandardiseResult>(`${BASE}/standardise`, data),

  // Check if a structure already exists in the registry
  checkUniqueness: (data: UniquenessRequest) =>
    apiClient.post<UniquenessResult>(`${BASE}/check-uniqueness`, data),

  // Register a new compound
  register: (data: RegisterRequest) =>
    apiClient.post<RegisterResult>(`${BASE}/register`, data),

  // List registered compounds with pagination and optional filters
  getCompounds: (params?: { limit?: number; offset?: number; status?: string; therapeutic_area?: string; search?: string }) =>
    apiClient.get<RegisteredCompound[]>(`${BASE}/compounds`, { params }),

  // Get a single compound with full version/lot tree
  getCompound: (parentCode: string) =>
    apiClient.get<RegisteredCompound>(`${BASE}/compounds/${parentCode}`),

  // Select a version as candidate (status progression)
  selectCandidate: (parentCode: string) =>
    apiClient.post(`${BASE}/compounds/${parentCode}/select-candidate`),

  // Withdraw a compound (soft delete)
  withdraw: (compoundCode: string, reason: string) =>
    apiClient.post(`${BASE}/withdraw?compound_code=${encodeURIComponent(compoundCode)}`, { reason }),

  // Reinstate a withdrawn compound
  reinstate: (compoundCode: string, reason: string) =>
    apiClient.post(`${BASE}/reinstate?compound_code=${encodeURIComponent(compoundCode)}`, { reason }),

  // Promote to development
  promote: (compoundCode: string, reason: string) =>
    apiClient.post(`${BASE}/promote`, { compound_code: compoundCode, reason }),

  // Demote from development (Super Admin only)
  demote: (compoundCode: string, reason: string) =>
    apiClient.post(`${BASE}/demote`, { compound_code: compoundCode, reason }),

  // Assign INN name
  assignINN: (compoundCode: string, innName: string, innStem?: string) =>
    apiClient.post(`${BASE}/inn`, { compound_code: compoundCode, inn_name: innName, inn_stem: innStem }),

  // Update clinical trial info
  updateClinical: (compoundCode: string, nctId?: string, clinicalPhase?: string) =>
    apiClient.post(`${BASE}/clinical`, { compound_code: compoundCode, nct_id: nctId, clinical_phase: clinicalPhase }),

  // Bulk actions
  bulkWithdraw: (codes: string[], reason: string) =>
    apiClient.post(`${BASE}/bulk-withdraw`, { compound_codes: codes, reason }),

  bulkPromote: (codes: string[], reason: string) =>
    apiClient.post(`${BASE}/bulk-promote`, { compound_codes: codes, reason }),

  // Permanent delete (Super Admin only, must be withdrawn)
  permanentDelete: (code: string, reason: string) =>
    apiClient.post(`${BASE}/permanent-delete`, { compound_code: code, reason }),

  bulkPermanentDelete: (codes: string[], reason: string) =>
    apiClient.post(`${BASE}/bulk-permanent-delete`, { compound_codes: codes, reason }),

  // Legacy soft-delete (routes to withdraw)
  deleteCompound: (parentCode: string, reason?: string) =>
    apiClient.delete(`${BASE}/compounds/${parentCode}`, { data: { reason } }),

  // Legacy restore (routes to reinstate)
  restoreCompound: (parentCode: string) =>
    apiClient.post(`${BASE}/compounds/${parentCode}/restore`),

  // Add a lot to an existing version
  addLot: (versionCode: string, data?: { batch_number?: string; purity_percent?: number; ee_percent?: number; supplier?: string }) =>
    apiClient.post(`${BASE}/versions/${versionCode}/add-lot`, data),

  // Get business rules
  getRules: () =>
    apiClient.get<RegistrationRules>(`${BASE}/rules`),

  // Update business rules
  updateRules: (data: Partial<RegistrationRules>) =>
    apiClient.put<RegistrationRules>(`${BASE}/rules`, data),

  // Get salt dictionary
  getSaltDictionary: () =>
    apiClient.get<Salt[]>(`${BASE}/salt-dictionary`),

  // Toggle salt enabled/disabled
  updateSalt: (saltId: number, data: { enabled: boolean }) =>
    apiClient.put(`${BASE}/salt-dictionary/${saltId}`, data),

  // Add custom salt
  addSalt: (data: { name: string; smiles: string; formula: string; charge: number; category?: string }) =>
    apiClient.post(`${BASE}/salt-dictionary`, data),

  // Get therapeutic areas
  getTherapeuticAreas: () =>
    apiClient.get<TherapeuticArea[]>(`${BASE}/therapeutic-areas`),

  // Get registration statistics
  getStats: () =>
    apiClient.get<RegistrationStats>(`${BASE}/stats`),

  // Get staging queue
  getStaging: () =>
    apiClient.get<StagingItem[]>(`${BASE}/staging`),

  // Get staging badge counts
  getStagingCounts: () =>
    apiClient.get<StagingCounts>(`${BASE}/staging/counts`),

  // Get audit log
  getAudit: () =>
    apiClient.get<AuditEntry[]>(`${BASE}/audit`),
};
